import React, { createContext, FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useDebouncedCallback } from 'use-debounce';
import { Camera, MaterialSelection, ModelPlacement, ModifierSelection, Position, PropsetSelection, StillImageConfiguration, Animation } from "../models/StillImageConfiguration";
import { CartContext, OrderConfiguration } from "./CartContext";
import { BomModelService, BomModelViewDTO, ConfigurationAssets, ConfigurationService, MaterialService, MaterialViewDTO, MetadataDTO, ModelPackService, ModelPackViewDTO, ModelPlacementPointDTO, ModelService, ModelSurfaceDTO, ModelViewDTO, ModifierViewDTO, SceneCameraDTO, SceneCameraPlacementPointDTO, SceneModifierTargetDTO, ScenePlacementpointDTO, ScenePropsetDTO, ScenePropsetOptionDTO, SceneSurfaceDTO, TemplateViewDTO, WhitemodelHotspotDTO } from "../openapi/requests";
import { makeRandomId } from "../helpers/helpers";
import { DialogContext } from "./DialogContext";
import { GenerateODataFilter } from "../helpers/odataFunctions";
import { ConfigContext } from "./ConfigContext";

export const moodboardColoumns = 24;

export class MoodboardPosition {
    x: number;
    y: number;
    height: number;
    width: number;

    constructor(x: number, y: number, width: number, height: number) {
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;
    }

    public static fromPos(pos?: Position): MoodboardPosition | undefined {
        if (pos === undefined || pos == null) {
            return undefined;
        }

        return new MoodboardPosition(
            Math.round(pos.x1 * moodboardColoumns),
            Math.round(pos.y1 * moodboardColoumns),
            Math.round((pos.x2 - pos.x1) * moodboardColoumns),
            Math.round((pos.y2 - pos.y1) * moodboardColoumns),
        );
    }

    public toPos(): Position {
        return {
            x1: this.x / moodboardColoumns,
            x2: (this.x + this.width) / moodboardColoumns,
            y1: this.y / moodboardColoumns,
            y2: (this.y + this.height) / moodboardColoumns
        }
    }

    public contains(position: MoodboardPosition): boolean {
        if (
            (this.x < (position.x + position.width) && (this.x + this.width) > position.x) &&
            (this.y < (position.y + position.height) && (this.y + this.height) > position.y)
        ) {
            return true;
        }
        else {
            return false;
        }
    }
}

export interface Configuration {
    surfaces: SurfaceConfiguration[];
    propsets: PropsetConfiguration[];
    models: ModelConfiguration[];
    modifiers: ModifierConfiguration[];
    moodboard: {
        backgroundColor: string;
        previewPosition: MoodboardPosition;
    },
    cameras: ({ cameras: SceneCameraDTO[] } & Camera)[];
    animations: Animation[];
    batches: BatchGroup[];
}

export interface BatchGroup {
    points: BatchGroupPoint[];
}

export interface BatchGroupPoint {
    type: "model" | "bommodel" | "modelpack" | "material" | "modifier" | "propset",
    path: string[],
    values: Asset[],
}

export interface ItemConfigurationValue<AssetT, PlacementT> {
    placement: PlacementT;
    values: AssetT[];
    parent: ModelViewDTO | ModelPackViewDTO | undefined;
}

export class ItemConfiguration<AssetT, PlacementT extends { label: string, isRequired?: boolean }> {
    protected _values: ItemConfigurationValue<AssetT, PlacementT>[];
    protected _uniqueValues: AssetT[];
    moodboardPosition?: MoodboardPosition;
    id: string;
    path: string[];
    hasChanged: boolean;
    hideInMoodboard: boolean;

    get label(): string {
        return this._values[0].placement.label;
    }

    get valueLabel(): string {
        return "";
    }

    get isRequired(): boolean {
        return this._values[0].placement.isRequired ?? true;
    }

    get value(): AssetT | undefined {
        if (this._uniqueValues.length) {
            return this._uniqueValues[0];
        }
        return undefined;
    }

    get values(): ItemConfigurationValue<AssetT, PlacementT>[] {
        return this._values;
    }

    get uniqueValues(): AssetT[] {
        return this._uniqueValues;
    }

    get placement(): PlacementT {
        return this.values[0].placement;
    }

    get placements(): PlacementT[] {
        return this.values.map(e => e.placement);
    }

    constructor(placements: ItemConfigurationValue<AssetT, PlacementT>[]) {
        if (placements.length === 0) {
            throw new Error("placements must have at least 1 item");
        }

        this._values = placements;
        var items = placements.flatMap(e => e.values);
        this._uniqueValues = items.filter((value, index, array) => array.indexOf(value) === index);
        this.hasChanged = false;
        this.id = "";
        this.path = [];
        this.hideInMoodboard = false;
    }

    clearValue(): this {
        let clone = this.clone() as this;

        clone._values = this._values.map(e => ({ placement: e.placement, values: [], parent: e.parent }));
        clone._uniqueValues = [];
        clone.hasChanged = true;

        return clone;
    }

    setValue(placements: ItemConfigurationValue<AssetT, PlacementT>[]): this {
        if (placements.length === 0) {
            throw new Error("placements must have at least 1 item");
        }

        let clone = this.clone() as this;
        var items = placements.flatMap(e => e.values);

        clone._values = placements;
        clone._uniqueValues = items.filter((value, index, array) => array.indexOf(value) === index);
        clone.hasChanged = true;

        return clone;
    }

    clone(): any {
        throw new Error("not implemented");
    }

    serialize(): any {
        throw new Error("not implemented");
    }

    protected static findModel = (assets: ConfigurationAssets, nameOrID?: string | number): ModelViewDTO | undefined => {
        if (nameOrID === undefined) {
            return undefined;
        }

        if (typeof nameOrID === 'number') {
            return assets.models.find(e => e.id === nameOrID);
        }
        return assets.models.find(e => e.name === nameOrID);
    }

    protected static findModelpack = (assets: ConfigurationAssets, nameOrID?: string | number): ModelPackViewDTO | undefined => {
        if (nameOrID === undefined) {
            return undefined;
        }

        if (typeof nameOrID === 'number') {
            return assets.modelpacks.find(e => e.id === nameOrID);
        }
        return assets.modelpacks.find(e => e.name === nameOrID);
    }

    protected static findMaterial = (assets: ConfigurationAssets, nameOrID?: string | number): MaterialViewDTO | undefined => {
        if (nameOrID === undefined) {
            return undefined;
        }

        if (typeof nameOrID === 'number') {
            return assets.materials.find(e => e.id === nameOrID);
        }
        return assets.materials.find(e => e.name === nameOrID);
    }

    protected static findModifier = (assets: ConfigurationAssets, nameOrID?: string | number): ModifierViewDTO | undefined => {
        if (nameOrID === undefined) {
            return undefined;
        }

        if (typeof nameOrID === 'number') {
            return assets.modifiers.find(e => e.id === nameOrID);
        }
        return assets.modifiers.find(e => e.name === nameOrID);
    }
}

export class SurfaceConfiguration extends ItemConfiguration<MaterialViewDTO, SceneSurfaceDTO | ModelSurfaceDTO> {

    get valueLabel(): string {
        if(this.uniqueValues.length > 1){
            return `${this.uniqueValues.length} items selected`
        }
        if(this.value !== undefined){
            var ean = this.value.metadata.find(e => e.name === "ean");
            if(ean){
                return ean.value;
            }
            return this.value.title;
        }
        return "";
    }

    static deserialize(placements: { placement: SceneSurfaceDTO | ModelSurfaceDTO, parent: ModelViewDTO | ModelPackViewDTO | undefined }[],
        selection: MaterialSelection | undefined,
        defaultSelection: MaterialSelection | undefined,
        assets: ConfigurationAssets): SurfaceConfiguration {

        if (placements.length === 0) {
            throw new Error("placements must have at least 1 item");
        }

        let values: ItemConfigurationValue<MaterialViewDTO, SceneSurfaceDTO | ModelSurfaceDTO>[] = [];

        if (selection?.Values) {
            placements.forEach(p => {
                var placementSelection = selection!.Values!.filter(e => e.name === undefined || e.name === p.placement.name);
                var uniqueValues = [...new Set(placementSelection.map(e => e.value))];
                var items = uniqueValues.map(e => this.findMaterial(assets, e));
                values.push({ placement: p.placement, parent: p.parent, values: items.filter(e => e !== undefined) as MaterialViewDTO[] });
            });
        } else if (selection) {
            let value = this.findMaterial(assets, selection?.Value);
            values = placements.map(p => ({ placement: p.placement, parent: p.parent, values: value ? [value] : [] }));
        } else {
            values = placements.map(p => ({ placement: p.placement, parent: p.parent, values: [] }));
        }

        let config = new SurfaceConfiguration(values);

        config.hideInMoodboard = selection?.HideInMoodboard ?? false;
        config.moodboardPosition = MoodboardPosition.fromPos(selection?.MoodboardPosition);
        config.hasChanged = selection?.Value !== defaultSelection?.Value;

        return config;
    }

    serialize = (minimal: boolean = false): MaterialSelection => {
        let result: MaterialSelection = {
            Name: this.placement.name,
            Value: this.value?.name ?? "",
            HideInMoodboard: this.hideInMoodboard,
        }
        
        if (!minimal) {
            result = {
                ...result,
                Values: this.values?.flatMap(v => v.values.map(e => ({
                    name: v.placement.name,
                    value: e.name,
                    filenamePrefix: e.metadata.find(m => m.name.toLowerCase() === 'ean')?.value,
                    parentName: v.parent?.name
                }))),
                validParents: [...new Set(this.values.map(e => e.parent?.name).filter(e => e !== undefined))] as string[],
                MoodboardPosition: this.moodboardPosition?.toPos(),
                Id: this.id,
            }
        }

        return result;
    }

    clone(): SurfaceConfiguration {
        let clone = new SurfaceConfiguration(this.values);
        clone.moodboardPosition = this.moodboardPosition;
        clone.id = this.id;
        clone.path = this.path;
        clone.hasChanged = this.hasChanged;
        clone.hideInMoodboard = this.hideInMoodboard;
        return clone;
    }
}

export class ModelConfiguration extends ItemConfiguration<ModelViewDTO | ModelPackViewDTO, ScenePlacementpointDTO | ModelPlacementPointDTO> {
    surfaces: SurfaceConfiguration[];
    models: ModelConfiguration[];
    features: FeatureConfiguration[];

    constructor(placements: ItemConfigurationValue<ModelViewDTO | ModelPackViewDTO, ScenePlacementpointDTO | ModelPlacementPointDTO>[]) {
        super(placements);

        this.surfaces = [];
        this.models = [];
        this.features = [];
    }

    get valueLabel(): string {
        if(this.uniqueValues.length > 1){
            return `${this.uniqueValues.length} items selected`
        }
        if(this.value !== undefined){
            var ean = this.value.metadata.find(e => e.name === "ean");
            if(ean){
                return ean.value;
            }
            return this.value.title;
        }
        return "";
    }

    static deserialize(placements: { placement: ScenePlacementpointDTO | ModelPlacementPointDTO, parent: ModelViewDTO | ModelPackViewDTO | undefined }[],
        selection: ModelPlacement | undefined,
        defaultSelection: ModelPlacement | undefined,
        assets: ConfigurationAssets): ModelConfiguration {

        if (placements.length === 0) {
            throw new Error("placements must have at least 1 item");
        }

        let values: ItemConfigurationValue<ModelViewDTO | ModelPackViewDTO, ScenePlacementpointDTO | ModelPlacementPointDTO>[] = [];

        let placementPointsByLabel: { [key: string]: { placement: ModelPlacementPointDTO, parent: ModelViewDTO | ModelPackViewDTO }[] } = {};
        let surfacesByLabel: { [key: string]: { placement: ModelSurfaceDTO, parent: ModelViewDTO | ModelPackViewDTO }[] } = {};
        const modelsAdded: number[] = [];

        if (placements[0].placement.useModelpack) {
            if (selection?.Values) {

                placements.forEach(p => {
                    var placementSelection = selection!.Values!.filter(e => e.name === undefined || e.name === p.placement.name);
                    var uniqueValues = [...new Set(placementSelection.map(e => e.value))];
                    var items = uniqueValues.map(e => this.findModelpack(assets, e)).filter(e => e !== undefined);

                    items.forEach(item => {
                        if (item) {
                            let modelId = item.models.find(e => e.isMasterModel)?.modelId;

                            if (!modelId && item.models.length > 0) {
                                modelId = item.models[0].modelId;
                            }

                            let model = this.findModel(assets, modelId);

                            if (model && !modelsAdded.includes(model.id)) {
                                model.placementpoints.forEach(p => {
                                    if (placementPointsByLabel[p.label]) {
                                        placementPointsByLabel[p.label].push({ placement: p, parent: item! });
                                    } else {
                                        placementPointsByLabel[p.label] = [{ placement: p, parent: item! }];
                                    }
                                });
                                model.surfaces.forEach(s => {
                                    if (surfacesByLabel[s.label]) {
                                        surfacesByLabel[s.label].push({ placement: s, parent: item! });
                                    } else {
                                        surfacesByLabel[s.label] = [{ placement: s, parent: item! }];
                                    }
                                });
                                modelsAdded.push(model.id);
                            }
                        }
                    });

                    values.push({ placement: p.placement, parent: p.parent, values: items as ModelPackViewDTO[] });
                });
            } else if (selection) {
                let value = this.findModelpack(assets, selection?.Value);
                if (value) {
                    let modelId = value.models.find(e => e.isMasterModel)?.modelId;

                    if (!modelId && value.models.length > 0) {
                        modelId = value.models[0].modelId;
                    }

                    let model = this.findModel(assets, modelId);

                    if (model && !modelsAdded.includes(model.id)) {
                        model.placementpoints.forEach(p => {
                            if (placementPointsByLabel[p.label]) {
                                placementPointsByLabel[p.label].push({ placement: p, parent: value! });
                            } else {
                                placementPointsByLabel[p.label] = [{ placement: p, parent: value! }];
                            }
                        });
                        model.surfaces.forEach(s => {
                            if (surfacesByLabel[s.label]) {
                                surfacesByLabel[s.label].push({ placement: s, parent: value! });
                            } else {
                                surfacesByLabel[s.label] = [{ placement: s, parent: value! }];
                            }
                        });
                        modelsAdded.push(model.id);
                    }
                }
                values = placements.map(e => ({ placement: e.placement, parent: e.parent, values: value ? [value] : [] }));
            } else {
                values = placements.map(e => ({ placement: e.placement, parent: e.parent, values: [] }));
            }
        } else {
            if (selection?.Values) {

                placements.forEach(p => {
                    var placementSelection = selection!.Values!.filter(e => e.name === undefined || e.name === p.placement.name);
                    var uniqueValues = [...new Set(placementSelection.map(e => e.value))];
                    var items = uniqueValues.map(e => this.findModel(assets, e)).filter(e => e !== undefined);

                    items.forEach(item => {
                        if (item) {
                            if (!modelsAdded.includes(item.id)) {
                                item.placementpoints.forEach(p => {
                                    if (placementPointsByLabel[p.label]) {
                                        placementPointsByLabel[p.label].push({ placement: p, parent: item });
                                    } else {
                                        placementPointsByLabel[p.label] = [{ placement: p, parent: item }];
                                    }
                                });
                                item.surfaces.forEach(s => {
                                    if (surfacesByLabel[s.label]) {
                                        surfacesByLabel[s.label].push({ placement: s, parent: item });
                                    } else {
                                        surfacesByLabel[s.label] = [{ placement: s, parent: item }];
                                    }
                                });
                                modelsAdded.push(item.id);
                            }
                        }
                    });

                    values.push({ placement: p.placement, parent: p.parent, values: items as ModelViewDTO[] });
                });
            } else if (selection) {
                let value = this.findModel(assets, selection?.Value);
                if (value) {
                    value.placementpoints.forEach(p => {
                        if (placementPointsByLabel[p.label]) {
                            placementPointsByLabel[p.label].push({ placement: p, parent: value! });
                        } else {
                            placementPointsByLabel[p.label] = [{ placement: p, parent: value! }];
                        }
                    });
                    value.surfaces.forEach(s => {
                        if (surfacesByLabel[s.label]) {
                            surfacesByLabel[s.label].push({ placement: s, parent: value! });
                        } else {
                            surfacesByLabel[s.label] = [{ placement: s, parent: value! }];
                        }
                    });
                }
                values = placements.map(e => ({ placement: e.placement, parent: e.parent, values: value ? [value] : [] }));
            } else {
                values = placements.map(e => ({ placement: e.placement, parent: e.parent, values: [] }));
            }
        }

        let config = new ModelConfiguration(values);

        config.hideInMoodboard = selection?.HideInMoodboard ?? false;
        config.moodboardPosition = MoodboardPosition.fromPos(selection?.MoodboardPosition);
        config.hasChanged = selection?.Value !== defaultSelection?.Value;


        for (const key in surfacesByLabel) {
            if (Object.prototype.hasOwnProperty.call(surfacesByLabel, key)) {
                const surfaces = surfacesByLabel[key];
                config.surfaces.push(SurfaceConfiguration.deserialize(
                    surfaces,
                    selection?.SurfaceSelections.find(e => surfaces.some(s => s.placement.name === e.Name)),
                    defaultSelection?.SurfaceSelections.find(e => surfaces.some(s => s.placement.name === e.Name)),
                    assets))
            }
        }

        for (const key in placementPointsByLabel) {
            if (Object.prototype.hasOwnProperty.call(placementPointsByLabel, key)) {
                const points = placementPointsByLabel[key];
                config.models.push(ModelConfiguration.deserialize(
                    points,
                    selection?.ModelSelections.find(e => points.some(s => s.placement.name === e.Name)),
                    defaultSelection?.ModelSelections.find(e => points.some(s => s.placement.name === e.Name)),
                    assets))
            }
        }

        return config;
    }

    serialize = (minimal: boolean = false, includeEmptyValues: boolean = false): ModelPlacement => {
        let result: ModelPlacement = {
            FeatureSelections: this.features.map(e => ({ Name: e.name, Value: e.value })),
            ModelSelections: this.models.filter(e => e.value !== undefined || includeEmptyValues).map(e => e.serialize(minimal, includeEmptyValues)),
            Name: this.placement.name,
            SurfaceSelections: this.surfaces.filter(e => e.value !== undefined || includeEmptyValues).map(e => e.serialize(minimal)),
            Value: this.value?.name ?? "",
            HideInMoodboard: this.hideInMoodboard,
        }

        if (!minimal) {
            result = {
                ...result,
                Values: this.values?.flatMap(v => v.values.map(e => ({
                    name: v.placement.name,
                    value: e.name,
                    filenamePrefix: e.metadata.find(m => m.name.toLowerCase() === 'ean')?.value,
                    parentName: v.parent?.name
                }))),
                validParents: [...new Set(this.values.map(e => e.parent?.name).filter(e => e !== undefined))] as string[],
                MoodboardPosition: this.moodboardPosition?.toPos(),
                Id: this.id,
            }
        }

        return result;
    }

    clearValue(): this {
        let clone = Object.create(this) as this;

        clone._values = this._values.map(e => ({ placement: e.placement, values: [], parent: e.parent }));
        clone._uniqueValues = [];
        clone.hasChanged = true;
        clone.surfaces = [];
        clone.models = [];

        return clone;
    }

    clone(): ModelConfiguration {
        let clone = new ModelConfiguration(this.values);
        clone.surfaces = this.surfaces;
        clone.models = this.models;
        clone.moodboardPosition = this.moodboardPosition;
        clone.id = this.id;
        clone.path = this.path;
        clone.hasChanged = this.hasChanged;
        clone.hideInMoodboard = this.hideInMoodboard;
        return clone;
    }
}

export class ModifierConfiguration extends ItemConfiguration<ModifierViewDTO, SceneModifierTargetDTO> {

    get valueLabel(): string {
        if(this.uniqueValues.length > 1){
            return `${this.uniqueValues.length} items selected`
        }
        if(this.value !== undefined){
            var ean = this.value.metadata.find(e => e.name === "ean");
            if(ean){
                return ean.value;
            }
            return this.value.title;
        }
        return "";
    }

    static deserialize(placements: SceneModifierTargetDTO[],
        selection: ModifierSelection | undefined,
        defaultSelection: ModifierSelection | undefined,
        assets: ConfigurationAssets): ModifierConfiguration {

        if (placements.length === 0) {
            throw new Error("placements must have at least 1 item");
        }

        let values: ItemConfigurationValue<ModifierViewDTO, SceneModifierTargetDTO>[] = [];

        if (selection?.Values) {
            placements.forEach(p => {
                var placementSelection = selection!.Values!.filter(e => e.name === undefined || e.name === p.name);
                var uniqueValues = [...new Set(placementSelection.map(e => e.value))];
                var items = uniqueValues.map(e => this.findModifier(assets, e));
                values.push({ placement: p, parent: undefined, values: items.filter(e => e !== undefined) as ModifierViewDTO[] });
            });
        } else if (selection) {
            let value = this.findModifier(assets, selection?.Value);
            values = placements.map(placement => ({ placement, parent: undefined, values: value ? [value] : [] }));
        } else {
            values = placements.map(placement => ({ placement, parent: undefined, values: [] }));
        }

        let config = new ModifierConfiguration(values);

        config.hideInMoodboard = selection?.HideInMoodboard ?? false;
        config.moodboardPosition = MoodboardPosition.fromPos(selection?.MoodboardPosition);
        config.hasChanged = selection?.Value !== defaultSelection?.Value;

        return config;
    }

    serialize = (): ModifierSelection => {
        return {
            Name: this.placement.name,
            Value: this.value!.name,
            Values: this.values?.flatMap(v => v.values.map(e => ({
                name: v.placement.name,
                value: e.name,
                filenamePrefix: e.metadata.find(m => m.name.toLowerCase() === 'ean')?.value,
                parentName: v.parent?.name
            }))),
            MoodboardPosition: this.moodboardPosition?.toPos(),
            Id: this.id,
        }
    }

    clone(): ModifierConfiguration {
        let clone = new ModifierConfiguration(this.values);
        clone.moodboardPosition = this.moodboardPosition;
        clone.id = this.id;
        clone.path = this.path;
        clone.hasChanged = this.hasChanged;
        clone.hideInMoodboard = this.hideInMoodboard;
        return clone;
    }
}

export class PropsetConfiguration extends ItemConfiguration<ScenePropsetOptionDTO, ScenePropsetDTO> {

    get valueLabel(): string {
        if(this.uniqueValues.length > 1){
            return `${this.uniqueValues.length} items selected`
        }
        if(this.value !== undefined){
            return this.value.title;
        }
        return "";
    }

    static deserialize(placements: ScenePropsetDTO[],
        selection: PropsetSelection | undefined,
        defaultSelection: PropsetSelection | undefined): PropsetConfiguration {

        if (placements.length === 0) {
            throw new Error("placements must have at least 1 item");
        }

        let values: ItemConfigurationValue<ScenePropsetOptionDTO, ScenePropsetDTO>[] = [];

        if (selection?.Values) {
            placements.forEach(p => {
                var placementSelection = selection!.Values!.filter(e => e.name === undefined || e.name === p.name);
                var uniqueValues = [...new Set(placementSelection.map(e => e.value))];
                var items = uniqueValues.map(e => p.options.find(o => o.name === e));
                values.push({ placement: p, parent: undefined, values: items.filter(e => e !== undefined) as ScenePropsetOptionDTO[] });
            });
        } else if (selection) {
            var value = placements[0].options.find(e => e.name === selection!.Value);
            values = placements.map(placement => ({ placement, parent: undefined, values: value ? [value] : [] }));
        } else {
            values = placements.map(placement => ({ placement, parent: undefined, values: [] }));
        }

        let config = new PropsetConfiguration(values);

        config.hideInMoodboard = selection?.HideInMoodboard ?? false;
        config.moodboardPosition = MoodboardPosition.fromPos(selection?.MoodboardPosition);
        config.hasChanged = selection?.Value !== defaultSelection?.Value;

        return config;
    }

    serialize = (): PropsetSelection => {
        return {
            Name: this.placement.name,
            Value: this.value!.name,
            MoodboardPosition: this.moodboardPosition?.toPos(),
            Id: this.id,
            HideInMoodboard: this.hideInMoodboard,
        }
    }

    clone(): PropsetConfiguration {
        let clone = new PropsetConfiguration(this.values);
        clone.moodboardPosition = this.moodboardPosition;
        clone.id = this.id;
        clone.path = this.path;
        clone.hasChanged = this.hasChanged;
        clone.hideInMoodboard = this.hideInMoodboard;
        return clone;
    }
}

export interface FeatureConfiguration {
    name: string;
    value: string;
}

export type BaseAsset = {
    warning?: string;
}

export type ModelAsset = BaseAsset & ModelViewDTO & {
    type: "model";
    validFor: (ScenePlacementpointDTO | ModelPlacementPointDTO)[];
}

export type BomModelAsset = BaseAsset & BomModelViewDTO & {
    type: "bommodel";
    validFor: (ScenePlacementpointDTO | ModelPlacementPointDTO)[];
}

export type ModelpackAsset = BaseAsset & ModelPackViewDTO & {
    type: "modelpack";
    validFor: (ScenePlacementpointDTO | ModelPlacementPointDTO)[];
}

export type MaterialAsset = BaseAsset & MaterialViewDTO & {
    type: "material";
    validFor: (SceneSurfaceDTO | ModelSurfaceDTO)[];
}

export type ModifierAsset = BaseAsset & ModifierViewDTO & {
    type: "modifier";
    validFor: SceneModifierTargetDTO[];
}

export type PropsetAsset = BaseAsset & ScenePropsetOptionDTO & {
    type: "propset";
    validFor: ScenePropsetDTO[];
    tags: Array<string>;
    metadata: Array<MetadataDTO>;
}
export type Asset = ModelAsset | BomModelAsset | ModelpackAsset | MaterialAsset | ModifierAsset | PropsetAsset;

type Margins = {
    top: number;
    right: number;
    bottom: number;
    left: number;
}

interface BomData {
    points: WhitemodelHotspotDTO[];
}

type BasePoint = {
    label: string;
    x?: number;
    y?: number;
    isRequired: boolean;
    view?: Margins;
    offbrandProduct: boolean;
    offStylingProduct: boolean;
    parent?: Point;
    children: Point[];
    bomData?: BomData;
    hidden: boolean;
}

export type ModelPoint = BasePoint & {
    type: "model";
    configuration: ModelConfiguration;
}
export type MaterialPoint = BasePoint & {
    type: "material";
    configuration: SurfaceConfiguration;
}
export type ModifierPoint = BasePoint & {
    type: "modifier";
    configuration: ModifierConfiguration;
}
export type PropsetPoint = BasePoint & {
    type: "propset";
    configuration: PropsetConfiguration;
}
export type Point = ModelPoint | MaterialPoint | ModifierPoint | PropsetPoint;

export type CameraView = SceneCameraDTO & {
    latestRendering: string | undefined;
}

export interface SceneDesignerContextState {
    loading: boolean;
    template: TemplateViewDTO | undefined;
    selectedCamera: CameraView | undefined;
    setSelectedCamera: (cam: SceneCameraDTO | undefined) => void;
    configuration: Configuration;
    changeProduct: (point: Point, products: Asset[], force: boolean) => Promise<boolean>;
    setConfiguration: (config: Configuration, dontAddToHistory?: boolean) => void;
    undo: () => void;
    redo: () => void;
    canUndo: boolean;
    canRedo: boolean;
    reset: () => void;
    products: Point[];
    batchProducts: Point[];
    selectedBrand: string | undefined;
    setSelectedBrand: (brand: string | undefined) => void;
    selectedStyleDirection: string | undefined;
    setSelectedStyleDirection: (styleDirection: string | undefined) => void;
};

export const emptyConfiguration: Configuration = {
    models: [],
    modifiers: [],
    propsets: [],
    surfaces: [],
    moodboard: {
        backgroundColor: '#fff',
        previewPosition: new MoodboardPosition(0, 0, 3, 3)
    },
    cameras: [],
    animations: [],
    batches: []
}

const contextDefaultValues: SceneDesignerContextState = {
    loading: false,
    template: undefined,
    selectedCamera: undefined,
    setSelectedCamera: () => { },
    configuration: emptyConfiguration,
    setConfiguration: () => { },
    undo: () => { },
    redo: () => { },
    canUndo: false,
    canRedo: false,
    reset: () => { },
    products: [],
    batchProducts: [],
    selectedBrand: undefined,
    setSelectedBrand: () => { },
    selectedStyleDirection: undefined,
    setSelectedStyleDirection: () => { },
    changeProduct: () => Promise.reject(),
};

export const SceneDesignerContext = createContext<SceneDesignerContextState>(
    contextDefaultValues
);

export const SceneDesignerProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
    const { error } = useContext(DialogContext);
    const { currentProject, currentOrder, updateCurrentOrder, newestDelivery } = useContext(CartContext);
    const { odataAssetFilters, odataFilters } = useContext(ConfigContext);

    const [loading, setLoading] = useState(false);
    const [template, setTemplate] = useState<TemplateViewDTO | undefined>(undefined);
    const [selectedCameraInternal, setSelectedCamera] = useState<SceneCameraDTO | undefined>(undefined);
    const [configuration, internalSetConfiguration] = useState(emptyConfiguration);
    const [selectedBrand, setSelectedBrand] = useState<string | undefined>(undefined);
    const [selectedStyleDirection, setSelectedStyleDirection] = useState<string | undefined>(undefined);

    const [undoHistory, setUndoHistory] = useState<Configuration[]>([]);
    const [redoHistory, setRedoHistory] = useState<Configuration[]>([]);

    const canUndo = undoHistory.length > 0;
    const canRedo = redoHistory.length > 0;

    const selectedCamera = useMemo(() => {
        if (selectedCameraInternal) {
            let latestRendering: string | undefined = undefined;

            for (let i = 0; i < newestDelivery.length; i++) {
                const orderline = newestDelivery[i];

                const config = JSON.parse(orderline.configurationSpec) as StillImageConfiguration;

                if (config.camera === selectedCameraInternal.cameraName && orderline.files.length > 0) {
                    latestRendering = orderline.files[0].url;
                }
            }

            return { latestRendering, ...selectedCameraInternal };
        }

        return undefined;
    }, [newestDelivery, selectedCameraInternal]);

    const products = useMemo(() => {
        const result: Point[] = [];

        const isOffbrand = (asset: ModelConfiguration | SurfaceConfiguration | ModifierConfiguration) => {
            if (asset.value === undefined) {
                return false;
            }

            if (selectedBrand) {
                return !asset.uniqueValues.some(e =>
                    !e.metadata.some(m => m.name === 'brand') ||
                    e.metadata.some(m => m.name === 'brand' && m.value.toLowerCase() === selectedBrand.toLowerCase()))
            }
            return false;
        }
        const isOffStyling = (asset: ModelConfiguration | SurfaceConfiguration | ModifierConfiguration) => {
            if (asset.value === undefined) {
                return false;
            }

            if (selectedStyleDirection) {
                return !asset.uniqueValues.some(e =>
                    !e.metadata.some(m => m.name === 'style_direction') ||
                    e.metadata.some(m => m.name === 'style_direction' && m.value.toLowerCase() === selectedStyleDirection.toLowerCase()))
            }
            return false;
        }

        const flatten = (model: ModelConfiguration, parent: Point) => {
            for (let i = 0; i < model.surfaces.length; i++) {
                var MaterialPoint: MaterialPoint = {
                    configuration: model.surfaces[i],
                    isRequired: true,
                    label: model.surfaces[i].label,
                    type: 'material',
                    offbrandProduct: isOffbrand(model.surfaces[i]),
                    offStylingProduct: isOffStyling(model.surfaces[i]),
                    view: parent.view,
                    parent: parent,
                    children: [],
                    hidden: model.surfaces[i].hideInMoodboard,
                };
                parent.children.push(MaterialPoint);
                result.push(MaterialPoint);
            }
            for (let i = 0; i < model.models.length; i++) {
                var modelPoint: ModelPoint = {
                    configuration: model.models[i],
                    isRequired: model.models[i].values[0].placement.isRequired,
                    label: model.models[i].label,
                    offbrandProduct: isOffbrand(model.models[i]),
                    offStylingProduct: isOffStyling(model.models[i]),
                    type: 'model',
                    view: parent.view,
                    parent: parent,
                    children: [],
                    hidden: model.models[i].hideInMoodboard,
                };

                const value = modelPoint.configuration.value as ModelViewDTO;

                if (value && value.isWhitemodel) {
                    modelPoint.bomData = { points: value.hotspots };
                }
                parent.children.push(modelPoint);
                result.push(modelPoint);
                flatten(model.models[i], modelPoint);
            }
        }

        for (let i = 0; i < configuration.surfaces.length; i++) {

            let point: Point = {
                configuration: configuration.surfaces[i],
                isRequired: true,
                label: configuration.surfaces[i].label,
                offbrandProduct: isOffbrand(configuration.surfaces[i]),
                offStylingProduct: isOffStyling(configuration.surfaces[i]),
                type: 'material',
                parent: undefined,
                children: [],
                hidden: configuration.surfaces[i].hideInMoodboard,
            };

            if (selectedCamera && template) {
                const surface = point.configuration.placement as SceneSurfaceDTO;
                const position = selectedCamera.surfacePoints.find(e => e.surface === surface.id);

                if (position) {
                    point.view = {
                        left: Math.min(position.rectX1, position.rectX2) * 100,
                        right: 100 - (Math.max(position.rectX1, position.rectX2) * 100),
                        top: Math.min(position.rectY1, position.rectY2) * 100,
                        bottom: 100 - (Math.max(position.rectY1, position.rectY2) * 100),
                    }
                    point.x = position.posX;
                    point.y = position.posY;
                }
            }

            result.push(point);
        }

        for (let i = 0; i < configuration.modifiers.length; i++) {
            let point: Point = {
                configuration: configuration.modifiers[i],
                isRequired: configuration.modifiers[i].isRequired,
                label: configuration.modifiers[i].label,
                offbrandProduct: isOffbrand(configuration.modifiers[i]),
                offStylingProduct: isOffStyling(configuration.modifiers[i]),
                type: 'modifier',
                parent: undefined,
                children: [],
                hidden: configuration.modifiers[i].hideInMoodboard,
            };

            if (selectedCamera && template) {
                const modifier = point.configuration.placement;
                const position = selectedCamera.modifierPoints.find(e => e.modifierTarget === modifier.id);

                if (position) {
                    point.view = {
                        left: Math.min(position.rectX1, position.rectX2) * 100,
                        right: 100 - (Math.max(position.rectX1, position.rectX2) * 100),
                        top: Math.min(position.rectY1, position.rectY2) * 100,
                        bottom: 100 - (Math.max(position.rectY1, position.rectY2) * 100),
                    }
                    point.x = position.posX;
                    point.y = position.posY;
                }
            }

            result.push(point);
        }

        for (let i = 0; i < configuration.propsets.length; i++) {
            if (configuration.propsets[i].label.toLowerCase() === "lighting") {
                continue;
            }

            let point: Point = {
                configuration: configuration.propsets[i],
                isRequired: configuration.propsets[i].isRequired,
                label: configuration.propsets[i].label,
                offbrandProduct: false,
                offStylingProduct: false,
                type: 'propset',
                parent: undefined,
                children: [],
                hidden: configuration.propsets[i].hideInMoodboard,
            };

            if (selectedCamera && template) {
                const propset = point.configuration.placement;
                const position = selectedCamera.propsetPoints.find(e => e.propset === propset.id);

                if (position) {
                    point.view = {
                        left: Math.min(position.rectX1, position.rectX2) * 100,
                        right: 100 - (Math.max(position.rectX1, position.rectX2) * 100),
                        top: Math.min(position.rectY1, position.rectY2) * 100,
                        bottom: 100 - (Math.max(position.rectY1, position.rectY2) * 100),
                    }
                    point.x = position.posX;
                    point.y = position.posY;
                }
            }

            result.push(point);
        }

        for (let i = 0; i < configuration.models.length; i++) {
            let point: Point = {
                configuration: configuration.models[i],
                isRequired: configuration.models[i].isRequired,
                label: configuration.models[i].label,
                offbrandProduct: isOffbrand(configuration.models[i]),
                offStylingProduct: isOffStyling(configuration.models[i]),
                type: 'model',
                parent: undefined,
                children: [],
                hidden: configuration.models[i].hideInMoodboard,
            };

            const value = point.configuration.value as ModelViewDTO;

            if (value && value.isWhitemodel) {
                point.bomData = { points: value.hotspots };
            }

            let position: SceneCameraPlacementPointDTO | undefined = undefined;

            if (selectedCamera && template) {
                const model = point.configuration.placement;
                position = selectedCamera.placementPoints.find(e => e.placementPoint === model.id);

                if (position) {
                    point.view = {
                        left: Math.min(position.rectX1, position.rectX2) * 100,
                        right: 100 - (Math.max(position.rectX1, position.rectX2) * 100),
                        top: Math.min(position.rectY1, position.rectY2) * 100,
                        bottom: 100 - (Math.max(position.rectY1, position.rectY2) * 100),
                    }
                    point.x = position.posX;
                    point.y = position.posY;
                }
            }

            result.push(point);


            flatten(configuration.models[i], point);
        }

        return result;
    }, [selectedBrand, selectedStyleDirection, configuration, selectedCamera, template]);

    const batchProducts = useMemo(() => {
        return products.filter(e => e.configuration.uniqueValues.length > 1);
    }, [products]);

    useEffect(() => {
        setTemplate(undefined);
        setSelectedCamera(undefined);
        internalSetConfiguration(emptyConfiguration);

        if (currentOrder?.configurations) {

            for (let index = 0; index < currentOrder.configurations.length; index++) {
                const element = currentOrder.configurations[index];

                //if (element.template.editorModule === 'generic_3d_scene') {
                    setTemplate(element.template);
                    setUndoHistory([]);
                    setRedoHistory([]);

                    if (element.configuration) {
                        load(element);
                    }

                    break;
                //}
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentProject, currentOrder?.order.id]);

    const ensureMoodboardPositions = useCallback((config: Configuration) => {

        const occupiedPositions: MoodboardPosition[] = [config.moodboard.previewPosition];
        const items: (ModelConfiguration | SurfaceConfiguration | ModifierConfiguration | PropsetConfiguration)[] = [];

        const isPositionOccupied = (position: MoodboardPosition): boolean => {
            return occupiedPositions.some(e => e.contains(position));
        }

        const getNextFreePosition = (): MoodboardPosition => {
            for (let y = 0; y < 50; y++) {
                for (let x = 0; x < moodboardColoumns - 2; x++) {
                    const pos = new MoodboardPosition(x, y, 3, 3);

                    if (!isPositionOccupied(pos)) {
                        return pos;
                    }
                }
            }

            return new MoodboardPosition(0, 0, 3, 3);
        }

        const flatten = (model: ModelConfiguration) => {
            let index = 1;


            const surfaces = [...model.surfaces].sort((a, b) => (parseInt(a.id) || 999) - (parseInt(b.id) || 999));

            for (let i = 0; i < surfaces.length; i++) {

                surfaces[i].id = model.id + "." + index.toString();
                surfaces[i].path = [...model.path, model.id];
                index++;

                items.push(surfaces[i]);
            }

            const models = [...model.models].sort((a, b) => (parseInt(a.id) || 999) - (parseInt(b.id) || 999));

            for (let i = 0; i < models.length; i++) {

                models[i].id = model.id + "." + index.toString();
                models[i].path = [...model.path, model.id];
                index++;

                items.push(models[i]);
                flatten(models[i]);
            }
        }

        let index = 1;

        const surfaces = [...config.surfaces].sort((a, b) => (parseInt(a.id) || 999) - (parseInt(b.id) || 999));

        for (let i = 0; i < surfaces.length; i++) {

            surfaces[i].id = index.toString();
            surfaces[i].path = [];
            index++;

            items.push(surfaces[i]);
        }

        const modifiers = [...config.modifiers].sort((a, b) => (parseInt(a.id) || 999) - (parseInt(b.id) || 999));

        for (let i = 0; i < modifiers.length; i++) {

            modifiers[i].id = index.toString();
            modifiers[i].path = [];
            index++;

            items.push(modifiers[i]);
        }

        const propsets = [...config.propsets].sort((a, b) => (parseInt(a.id) || 999) - (parseInt(b.id) || 999));

        for (let i = 0; i < propsets.length; i++) {

            if (propsets[i].label.toLowerCase() === "lighting") {
                continue;
            }

            propsets[i].id = index.toString();
            propsets[i].path = [];
            index++;

            items.push(propsets[i]);
        }

        const models = [...config.models].sort((a, b) => (parseInt(a.id) || 999) - (parseInt(b.id) || 999));

        for (let i = 0; i < models.length; i++) {

            models[i].id = index.toString();
            models[i].path = [];

            items.push(models[i]);

            flatten(models[i]);
            index++;
        }

        for (let i = 0; i < items.length; i++) {
            const item = items[i];

            if (item.moodboardPosition && !item.hideInMoodboard) {
                occupiedPositions.push(item.moodboardPosition);
            }
        }

        for (let i = 0; i < items.length; i++) {
            const item = items[i];

            if (!item.moodboardPosition && !item.hideInMoodboard) {
                item.moodboardPosition = getNextFreePosition();
                occupiedPositions.push(item.moodboardPosition);
            }
        }

    }, []);

    const save = useCallback(() => {

        let config: StillImageConfiguration = {
            camera: selectedCamera!.cameraName,
            PropsetSelections: configuration.propsets.filter(e => e.value !== undefined).map(e => e.serialize()),
            ModifierSelections: configuration.modifiers.filter(e => e.value !== undefined).map(e => e.serialize()),
            SurfaceSelections: configuration.surfaces.filter(e => e.value !== undefined).map(e => e.serialize()),
            ModelSelections: configuration.models.filter(e => e.value !== undefined).map(e => e.serialize()),
            Moodboard: {
                BackgroundColor: configuration.moodboard.backgroundColor,
                PreviewPosition: configuration.moodboard.previewPosition.toPos(),
            },
            Cameras: configuration.cameras.map(e => ({
                Name: e.Name,
                Variants: e.Variants,
                Formats: e.Formats
            })),
            Animations: configuration.animations.map(e => ({
                Name: e.Name,
                Shots: e.Shots,
                Variants: e.Variants,
                Title: e.Title,
                Aspect: e.Aspect,
            }))
        }

        config = {...config,
            Batches: configuration.batches.map(batch => ({
                Selections: batch.points.map(p => ({
                    Type: p.type,
                    Path: p.path,
                    Values: p.values.map(e => e.name),
                }))
            }))
        }

        updateCurrentOrder({ configurations: currentOrder?.configurations.map(e => (e.template.id === template?.id) ? { ...e, configuration: config } : e) })
            .catch(reason => {
                error(undefined, reason);
            });


    }, [configuration, selectedCamera, updateCurrentOrder, template, currentOrder, error]);

    const debouncedSave = useDebouncedCallback(save, 1000);

    const mapConfiguration = useCallback(async (order: OrderConfiguration): Promise<Configuration> => {


        let defaultConfig: StillImageConfiguration = {
            camera: '',
            ModelSelections: [],
            ModifierSelections: [],
            PropsetSelections: [],
            SurfaceSelections: [],
        };

        if (order.template.scene.defaultConfigurationSpec && order.template.scene.defaultConfigurationSpec !== "") {
            defaultConfig = JSON.parse(order.template.scene.defaultConfigurationSpec);
        }

        var configItems = await ConfigurationService.postConfigurationItems(order.template.clientId, JSON.stringify(JSON.stringify(order.configuration)));

        const configuration: Configuration = {
            surfaces: order.template.scene.surfaces.map(surface => SurfaceConfiguration.deserialize(
                [{ placement: surface, parent: undefined }],
                order.configuration.SurfaceSelections.find(e => e.Name === surface.name),
                defaultConfig.SurfaceSelections.find(e => e.Name === surface.name),
                configItems)),
            propsets: order.template.scene.propsets.map(propset => PropsetConfiguration.deserialize(
                [propset],
                order.configuration.PropsetSelections.find(e => e.Name === propset.name),
                defaultConfig.PropsetSelections.find(e => e.Name === propset.name))),
            modifiers: order.template.scene.modifierTargets.map(modifiertarget => ModifierConfiguration.deserialize(
                [modifiertarget],
                order.configuration.ModifierSelections.find(e => e.Name === modifiertarget.name),
                defaultConfig.ModifierSelections.find(e => e.Name === modifiertarget.name),
                configItems)),
            models: order.template.scene.placementpoints.map(placement => ModelConfiguration.deserialize(
                [{ placement, parent: undefined }],
                order.configuration.ModelSelections.find(e => e.Name === placement.name),
                defaultConfig.ModelSelections.find(e => e.Name === placement.name),
                configItems)),
            moodboard: {
                backgroundColor: order.configuration.Moodboard?.BackgroundColor ?? emptyConfiguration.moodboard.backgroundColor,
                previewPosition: MoodboardPosition.fromPos(order.configuration.Moodboard?.PreviewPosition ?? { x1: 0, x2: 0.375, y1: 0, y2: 0.375 })!,
            },
            cameras: order.configuration.Cameras ? order.configuration.Cameras.map(e => ({
                cameras: order.template.scene.cameras.filter(c => c.cameraName === e.Name)!,
                Name: e.Name,
                Variants: e.Variants,
                Formats: e.Formats,
            })).filter(e => e.cameras.length !== 0) : [],
            animations: order.configuration.Animations ? order.configuration.Animations.map(e => ({
                animation: order.template.scene.animations.find(c => c.name === e.Name)!,
                Name: e.Name || makeRandomId(8),
                Variants: e.Variants || [],
                Shots: e.Shots,
                Title: e.Title || '',
                Aspect: e.Aspect,
            })) : [],
            batches: []
        }

        if(order.configuration.Batches){
            for (let i = 0; i < order.configuration.Batches.length; i++) {
                const batch = order.configuration.Batches[i];
                
                let batchGroup: BatchGroup = {
                    points: []
                }

                if(batch.Selections){
                    for (let j = 0; j < batch.Selections.length; j++) {
                        const selection = batch.Selections[j];
                        const point:BatchGroupPoint = {
                            path: selection.Path,
                            type: selection.Type,
                            values: [],
                        };

                        switch(selection.Type){
                            case "model":
                                point.values = configItems.models.filter(e => selection.Values.includes(e.name)).map(e => ({...e, type: "model", validFor: []}));
                                break;
                            case "modelpack":
                                point.values = configItems.modelpacks.filter(e => selection.Values.includes(e.name)).map(e => ({...e, type: "modelpack", validFor: []}));
                                break;
                            case "material":
                                point.values = configItems.materials.filter(e => selection.Values.includes(e.name)).map(e => ({...e, type: "material", validFor: []}));
                                break;
                            case "modifier":
                                point.values = configItems.modifiers.filter(e => selection.Values.includes(e.name)).map(e => ({...e, type: "modifier", validFor: []}));
                                break;
                            case "propset":
                                const propset = order.template.scene.propsets.find(e => e.name === selection.Path[0]);
                                if(propset){
                                    point.values = propset.options.filter(e => selection.Values.includes(e.name)).map(e => ({...e, type: "propset", validFor: [], tags: [], metadata: []}));
                                }
                                break;
                        }

                        if(point.values.length > 0){
                            batchGroup.points.push(point);
                        }
                    }
                }

                if(batchGroup.points.length > 0){
                    configuration.batches.push(batchGroup);
                }
            }
        }

        return configuration;
    }, []);

    const load = useCallback((order: OrderConfiguration) => {
        setLoading(true);

        const _load = async () => {
            internalSetConfiguration(emptyConfiguration);


            let configuration = await mapConfiguration(order);


            var cam = configuration.cameras[0]?.cameras[0];
            if (!cam) {
                cam = order.template.scene.cameras[0];
            }
            setSelectedCamera(cam);

            ensureMoodboardPositions(configuration);

            internalSetConfiguration(configuration);
        }

        _load().then(() => {
            setUndoHistory([]);
            setRedoHistory([]);
            setLoading(false);
        });

    }, [mapConfiguration, ensureMoodboardPositions]);

    const setConfiguration = useCallback((config: Configuration, dontAddToHistory: boolean = false) => {
        ensureMoodboardPositions(config);

        if (!dontAddToHistory) {
            const history = [...undoHistory, configuration].slice(-50);
            setUndoHistory(history);
            setRedoHistory([]);
        }

        internalSetConfiguration(config);

        debouncedSave();

    }, [configuration, debouncedSave, ensureMoodboardPositions, undoHistory]);

    const reset = useCallback(async () => {
        if (template) {
            const history = [...undoHistory, configuration].slice(-50);

            let config: StillImageConfiguration = {
                camera: '',
                ModelSelections: [],
                ModifierSelections: [],
                PropsetSelections: [],
                SurfaceSelections: [],
                Cameras: [],
            };

            if (template.scene.defaultConfigurationSpec !== undefined && template.scene.defaultConfigurationSpec !== null && template.scene.defaultConfigurationSpec !== '') {
                config = JSON.parse(template.scene.defaultConfigurationSpec);
            }

            const newConfiguration: Configuration = {
                ...await mapConfiguration({ configuration: config, template }),
                cameras: configuration.cameras,
            }

            var cam = configuration.cameras[0]?.cameras[0];
            if (!cam) {
                cam = template.scene.cameras[0];
            }
            setSelectedCamera(cam);

            ensureMoodboardPositions(newConfiguration);

            setUndoHistory(history);
            internalSetConfiguration(newConfiguration);
            setRedoHistory([]);

            debouncedSave();
        }

    }, [configuration, debouncedSave, ensureMoodboardPositions, mapConfiguration, template, undoHistory]);

    const undo = useCallback(() => {
        if (undoHistory.length > 0) {
            const newConfig = undoHistory[undoHistory.length - 1];

            const newUndoHistory = [...undoHistory].slice(0, -1);
            setUndoHistory(newUndoHistory);

            const newRedoHistory = [...redoHistory, configuration];
            setRedoHistory(newRedoHistory);

            internalSetConfiguration(newConfig);

            debouncedSave();
        }
    }, [configuration, debouncedSave, redoHistory, undoHistory]);

    const redo = useCallback(() => {
        if (redoHistory.length > 0) {
            const newConfig = redoHistory[redoHistory.length - 1];

            const newUndoHistory = [...undoHistory, configuration];
            setUndoHistory(newUndoHistory);

            const newRedoHistory = [...redoHistory].slice(0, -1);
            setRedoHistory(newRedoHistory);

            internalSetConfiguration(newConfig);

            debouncedSave();
        }
    }, [configuration, debouncedSave, redoHistory, undoHistory]);

    const changeProduct = useCallback(async (point: Point, products: Asset[], force: boolean) => {

        var newConfig = point.configuration;

        if (point.type === "material") {
            const placements = point.configuration.values.map(v => ({
                placement: v.placement,
                parent: v.parent,
                values: products.filter(e => e.validFor.some(e => e.name === v.placement.name)) as MaterialAsset[]
            }));
            newConfig = point.configuration.setValue(placements);
        } else if (point.type === 'modifier') {
            const placements = point.configuration.values.map(v => ({
                placement: v.placement,
                parent: v.parent,
                values: products.filter(e => e.validFor.some(e => e.name === v.placement.name)) as ModifierAsset[]
            }));
            newConfig = point.configuration.setValue(placements);
        } else if (point.type === 'propset') {
            const placements = point.configuration.values.map(v => ({
                placement: v.placement,
                parent: v.parent,
                values: products.filter(e => e.validFor.some(e => e.name === v.placement.name)) as PropsetAsset[]
            }));

            newConfig = point.configuration.setValue(placements);
        } else if (point.type === 'model') {
            var bommodel = products.find(e => e.type === "bommodel") as BomModelAsset | undefined;

            if (bommodel) {
                var config = JSON.parse(bommodel.configuration) as ModelPlacement;
                var configItems: ConfigurationAssets = {materials: [], modelpacks: [], models: [], modifiers: []};
                
                try {
                    configItems = await BomModelService.getBomModelAssets(bommodel.id);
                } catch {}
                
                const placements = point.configuration.values.map(v => ({
                    placement: v.placement,
                    parent: v.parent,
                    values: [bommodel].filter(e => e!.validFor.some(e => e.name === v.placement.name))
                }));
        
                newConfig = ModelConfiguration.deserialize(placements, config, undefined, configItems);
                newConfig.id = point.configuration.id;
            } else {
                let oldConfig = point.configuration;
                const models = products.filter(e => e.type === "model") as ModelAsset[];

                const placements = oldConfig.values.map(v => ({
                    placement: v.placement,
                    parent: v.parent,
                    values: products.filter(e => e.validFor.some(e => e.name === v.placement.name)) as (ModelAsset | ModelpackAsset)[]
                }));

                newConfig = oldConfig.setValue(placements);
                newConfig.models = [];
                newConfig.surfaces = [];

                let placementPointsByLabel: { [key: string]: { placement: ModelPlacementPointDTO, parent: ModelViewDTO | ModelPackViewDTO }[] } = {};
                let surfacesByLabel: { [key: string]: { placement: ModelSurfaceDTO, parent: ModelViewDTO | ModelPackViewDTO }[] } = {};
                const modelsAdded: number[] = [];


                for (let i = 0; i < newConfig.uniqueValues.length; i++) {
                    const asset = newConfig.uniqueValues[i];

                    if ((asset as ModelPackViewDTO).models) {
                        let modelpack = asset as ModelPackViewDTO;
                        var mastermodelID = modelpack.models[0].modelId;

                        var master = modelpack.models.find(e => e.isMasterModel);
                        if (master) {
                            mastermodelID = master.modelId;
                        }

                        let masterModel = await ModelService.getModel1(mastermodelID);

                        if (masterModel && !modelsAdded.includes(masterModel.id)) {
                            masterModel.placementpoints.forEach(p => {
                                if (placementPointsByLabel[p.label]) {
                                    placementPointsByLabel[p.label].push({ placement: p, parent: asset });
                                } else {
                                    placementPointsByLabel[p.label] = [{ placement: p, parent: asset }];
                                }
                            });
                            masterModel.surfaces.forEach(s => {
                                if (surfacesByLabel[s.label]) {
                                    surfacesByLabel[s.label].push({ placement: s, parent: asset });
                                } else {
                                    surfacesByLabel[s.label] = [{ placement: s, parent: asset }];
                                }
                            });
                            modelsAdded.push(masterModel.id);
                        }
                    } else {
                        let model = asset as ModelViewDTO;

                        if (!modelsAdded.includes(model.id)) {
                            model.placementpoints.forEach(p => {
                                if (placementPointsByLabel[p.label]) {
                                    placementPointsByLabel[p.label].push({ placement: p, parent: asset });
                                } else {
                                    placementPointsByLabel[p.label] = [{ placement: p, parent: asset }];
                                }
                            });
                            model.surfaces.forEach(s => {
                                if (surfacesByLabel[s.label]) {
                                    surfacesByLabel[s.label].push({ placement: s, parent: asset });
                                } else {
                                    surfacesByLabel[s.label] = [{ placement: s, parent: asset }];
                                }
                            });
                            modelsAdded.push(model.id);
                        }
                    }
                }

                for (const key in surfacesByLabel) {
                    if (Object.prototype.hasOwnProperty.call(surfacesByLabel, key)) {
                        const surfaces = surfacesByLabel[key];

                        const oldSurfaceConfig = oldConfig.surfaces.find(e => e.placement.label === surfaces[0].placement.label);
                        const placements: ItemConfigurationValue<MaterialViewDTO, SceneSurfaceDTO | ModelSurfaceDTO>[] = surfaces.map(e => ({ placement: e.placement, parent: e.parent, values: [] }));

                        if (oldSurfaceConfig !== undefined && oldSurfaceConfig.uniqueValues.length > 0) {

                            for (let i = 0; i < surfaces.length; i++) {
                                const surface = surfaces[i].placement;
                                let validMaterials = await MaterialService.getMaterial(surface.materialSelectionFilter, undefined, undefined, GenerateODataFilter(odataAssetFilters));

                                let placement = placements.find(e => e.placement.name === surface.name);
                                if (placement) {
                                    placement.values = oldSurfaceConfig.uniqueValues.filter(v => validMaterials.value.some(e => e.id === v.id));
                                }
                            }
                        }

                        const newSurfaceConfig = new SurfaceConfiguration(placements);
                        if(models.some(e => e.isWhitemodel)){
                            newSurfaceConfig.hideInMoodboard = true;
                        }
                        if(!oldSurfaceConfig && newConfig.hideInMoodboard){
                            newSurfaceConfig.hideInMoodboard = true;
                        }

                        if (oldSurfaceConfig && oldSurfaceConfig.uniqueValues.length > 0 && newSurfaceConfig.uniqueValues.length === 0) {
                            if (!force) {
                                return false;
                            }
                        }

                        newConfig.surfaces.push(newSurfaceConfig);
                    }
                }

                for (const key in placementPointsByLabel) {
                    if (Object.prototype.hasOwnProperty.call(placementPointsByLabel, key)) {
                        const points = placementPointsByLabel[key];

                        const oldModelConfig = oldConfig.models.find(e => e.placement.label === points[0].placement.label);
                        const placements: ItemConfigurationValue<ModelViewDTO | ModelPackViewDTO, ScenePlacementpointDTO | ModelPlacementPointDTO>[] = points.map(e => ({ placement: e.placement, parent: e.parent, values: [] }));

                        if (oldModelConfig !== undefined && oldModelConfig.uniqueValues.length > 0) {
                            for (let i = 0; i < points.length; i++) {
                                const point = points[i].placement;

                                if (point.useModelpack) {
                                    let placement = placements.find(e => e.placement.name === point.name);
                                    if (placement) {
                                        let validModelpacks = await ModelPackService.getModelPack(point.modelSelectionFilter, undefined, undefined, GenerateODataFilter(odataFilters));
                                        placement.values = oldModelConfig.uniqueValues.filter(v => validModelpacks.value.some(e => e.id === v.id));
                                    }
                                } else {
                                    let placement = placements.find(e => e.placement.name === point.name);
                                    if (placement) {
                                        let validModels = await ModelService.getModel(point.modelSelectionFilter, undefined, undefined, GenerateODataFilter(odataAssetFilters));
                                        placement.values = oldModelConfig.uniqueValues.filter(v => validModels.value.some(e => e.id === v.id));
                                    }
                                }
                            }
                        }

                        let newModelConfig = oldModelConfig ? oldModelConfig?.clone() : new ModelConfiguration(placements);
                        newModelConfig = newModelConfig.setValue(placements);
                        if(models.some(e => e.isWhitemodel)){
                            newModelConfig.hideInMoodboard = true;
                        }
                        if(!oldModelConfig && newConfig.hideInMoodboard){
                            newModelConfig.hideInMoodboard = true;
                        }

                        if (oldModelConfig && oldModelConfig.uniqueValues.length > 0 && newModelConfig.uniqueValues.length === 0) {
                            if (!force) {
                                return false;
                            }
                        }

                        if (newModelConfig.value === undefined) {
                            newModelConfig.models = [];
                            newModelConfig.surfaces = [];
                        }

                        /*
                        for (let i = 0; i < newModelConfig.models.length; i++) {
                            const element = newModelConfig.models[i];
                            const values = element.values.filter(e => newModelConfig.uniqueValues.some(p => p.id === e.parent?.id));
    
                            if(values.length === 0){
                                newModelConfig.models[i] = element.clearValue();
                            }else{
                                newModelConfig.models[i] = element.setValue(values);
                            }
                        }
    
                        for (let i = 0; i < newModelConfig.surfaces.length; i++) {
                            const element = newModelConfig.surfaces[i];
                            const values = element.values.filter(e => newModelConfig.uniqueValues.some(p => p.id === e.parent?.id));
    
                            if(values.length === 0){
                                newModelConfig.surfaces[i] = element.clearValue();
                            }else{
                                newModelConfig.surfaces[i] = element.setValue(values);
                            }
                        }
                        */

                        newConfig.models.push(newModelConfig);
                    }
                }
            }
        }


        if (point.configuration.path.length === 0) {
            if (newConfig instanceof SurfaceConfiguration) {
                const newConfiguration: Configuration = {
                    ...configuration,
                    surfaces: [...configuration.surfaces.filter(e => e.placement.name !== point.configuration.placement.name), newConfig]
                };
                setConfiguration(newConfiguration);
            }
            if (newConfig instanceof PropsetConfiguration) {
                const newConfiguration: Configuration = {
                    ...configuration,
                    propsets: [...configuration.propsets.filter(e => e.placement.name !== point.configuration.placement.name), newConfig]
                };
                setConfiguration(newConfiguration);
            }
            if (newConfig instanceof ModifierConfiguration) {
                const newConfiguration: Configuration = {
                    ...configuration,
                    modifiers: [...configuration.modifiers.filter(e => e.placement.name !== point.configuration.placement.name), newConfig]
                };
                setConfiguration(newConfiguration);
            }
            if (newConfig instanceof ModelConfiguration) {
                const newConfiguration: Configuration = {
                    ...configuration,
                    models: [...configuration.models.filter(e => e.placement.name !== point.configuration.placement.name), newConfig]
                };
                setConfiguration(newConfiguration);
            }
        } else {
            let parent: ModelConfiguration | undefined = configuration.models.find(e => e.id === point.configuration.path[0]);

            for (let i = 1; i < point.configuration.path.length; i++) {
                parent = parent?.models.find(e => e.id === point.configuration.path[i]);
            }

            if (parent) {
                if (newConfig instanceof ModelConfiguration) {
                    var modelConfig = newConfig;
                    parent.models = parent.models.map(e => e.id === point.configuration.id ? modelConfig : e);
                    setConfiguration({ ...configuration });
                }
                if (newConfig instanceof SurfaceConfiguration) {
                    var materialConfig = newConfig;
                    parent.surfaces = parent.surfaces.map(e => e.id === point.configuration.id ? materialConfig : e);
                    setConfiguration({ ...configuration });
                }
            }
        }

        return true;
    }, [configuration, odataAssetFilters, odataFilters, setConfiguration]);


    return (
        <SceneDesignerContext.Provider
            value={{
                loading,
                template,
                selectedCamera,
                setSelectedCamera,
                configuration,
                setConfiguration,
                undo,
                redo,
                canRedo,
                canUndo,
                reset,
                products,
                batchProducts,
                selectedBrand,
                setSelectedBrand,
                changeProduct,
                selectedStyleDirection,
                setSelectedStyleDirection
            }}
        >
            {children}
        </SceneDesignerContext.Provider>
    );
};